/**
 * Form Element Helper Utilities
 * Shared utilities for working with form elements
 */

/**
 * Extracts default value from a form element based on its type
 * @author Muskan Thakur
 */
export const getElementDefaultValue = (element) => {
  if (!element || !element.properties) return null;

  const { type, properties } = element;

  switch (type) {
    case "date":
      if (properties.defaultValue === "Current Date") {
        return new Date().toISOString();
      }
      return properties.defaultValue || null;

    case "dropdown":
      return properties.default && properties.default !== "" && properties.default !== null && properties.default !== undefined
        ? properties.default
        : null;

    case "text-label":
    case "text":
    case "input":
      return properties.defaultValue && properties.defaultValue !== "" && properties.defaultValue !== null && properties.defaultValue !== undefined
        ? properties.defaultValue
        : null;

    case "number":
      return properties.defaultValue !== null && properties.defaultValue !== undefined && properties.defaultValue !== ""
        ? properties.defaultValue
        : null;

    default:
      return properties.defaultValue !== null && properties.defaultValue !== undefined && properties.defaultValue !== ""
        ? properties.defaultValue
        : null;
  }
};

/**
 * Finds a form element by ID, searching both regular elements and grid elements
 * @author Muskan Thakur
 */
export const findElementById = (elementId, formData, hostSection = "subsections") => {
  if (!formData?.[hostSection] || !elementId) return null;

  /* First, check regular elements*/
  for (const subsection of formData[hostSection]) {
    if (!subsection.elements) continue;

    for (const element of subsection.elements) {
      if (element.id === elementId) {
        return element;
      }

      /* Check grid elements if this is a row-container*/
      if (element.type === "row-container" && element.gridElements) {
        for (const row of element.gridElements) {
          for (const gridElement of row) {
            if (gridElement && gridElement.id === elementId) {
              return gridElement;
            }
          }
        }
      }
    }
  }

  return null;
};

/**
 * Extracts all default values from form content
 * @author Muskan Thakur
 */
export const extractAllDefaultValues = (formContent, hostSection = "subsections") => {
  if (!formContent?.[hostSection]) return {};

  const defaultValues = {};

  const processElement = (element) => {
    if (!element || !element.properties) return;

    const defaultValue = getElementDefaultValue(element);
    if (defaultValue !== null && defaultValue !== undefined && element.id && !element.properties.isHidden) {
      defaultValues[element.id] = defaultValue;
    }

    /* Process grid elements recursively*/
    if (element.type === "row-container" && element.gridElements) {
      element.gridElements.forEach((row) => {
        row.forEach((gridElement) => {
          if (gridElement) {
            processElement(gridElement);
          }
        });
      });
    }
  };

  formContent[hostSection].forEach((subsection) => {
    if (subsection.elements) {
      subsection.elements.forEach((element) => {
        processElement(element);
      });
    }
  });

  return defaultValues;
};
